import { db } from './db';
import client from './api/client';

// Core sync lock to prevent concurrent sync operations
let isSyncing = false;

export const syncData = async () => {
  if (isSyncing) {
    console.log('Sync already in progress, skipping...');
    return { status: 'busy' };
  }

  isSyncing = true;
  console.log('=== STARTING OFFLINE-FIRST SYNC ENGINE ===');

  try {
    const lastSyncTime = localStorage.getItem('last_sync_time') || null;
    
    // Step 1: Push local modifications to the server
    const pushResult = await pushLocalChanges();
    
    // Step 2: Upload offline images for successfully pushed items
    await uploadOfflineImages(pushResult.itemIdMap);

    // Step 3: Pull latest changes from the server
    const pullResult = await pullServerChanges(lastSyncTime);

    // Save current server time as the next sync checkpoint
    if (pullResult.server_time) {
      localStorage.setItem('last_sync_time', pullResult.server_time);
    }

    console.log('=== SYNC COMPLETED SUCCESSFULLY ===');
    return { status: 'success', pushed: pushResult.count, pulled: pullResult.count };
  } catch (error) {
    console.error('=== SYNC FAILED ===', error);
    return { status: 'failed', error: error.message };
  } finally {
    isSyncing = false;
  }
};

/**
 * Pushes local additions, modifications, and deletions to the server.
 */
const pushLocalChanges = async () => {
  const pendingCategories = (await db.categories.toArray()).filter(c => c.sync_status === 'pending_upload' || c.sync_status === 'pending_delete');
  const pendingLocations = (await db.locations.toArray()).filter(l => l.sync_status === 'pending_upload' || l.sync_status === 'pending_delete');
  const pendingItems = (await db.items.toArray()).filter(i => i.sync_status === 'pending_upload' || i.sync_status === 'pending_delete');

  const deletedCategories = pendingCategories.filter(c => c.sync_status === 'pending_delete').map(c => String(c.id));
  const deletedLocations = pendingLocations.filter(l => l.sync_status === 'pending_delete').map(l => String(l.id));
  const deletedItems = pendingItems.filter(i => i.sync_status === 'pending_delete').map(i => String(i.id));

  const activeCategories = pendingCategories.filter(c => c.sync_status === 'pending_upload');
  const activeLocations = pendingLocations.filter(l => l.sync_status === 'pending_upload');
  const activeItems = pendingItems.filter(i => i.sync_status === 'pending_upload');

  const totalPending = activeCategories.length + activeLocations.length + activeItems.length +
                        deletedCategories.length + deletedLocations.length + deletedItems.length;

  if (totalPending === 0) {
    console.log('No local pending changes to push.');
    return { count: 0, itemIdMap: {} };
  }

  console.log(`Pushing ${totalPending} local pending changes to server...`);

  // Build push request payload
  const payload = {
    categories: activeCategories.map(c => ({
      id: String(c.id),
      name: c.name,
      updated_at: new Date(c.updated_at || Date.now()).toISOString()
    })),
    locations: activeLocations.map(l => ({
      id: String(l.id),
      name: l.name,
      parent_id: l.parent_id ? String(l.parent_id) : null,
      updated_at: new Date(l.updated_at || Date.now()).toISOString()
    })),
    items: activeItems.map(i => ({
      id: String(i.id),
      name: i.name,
      category_id: i.category_id ? String(i.category_id) : null,
      location_id: i.location_id ? String(i.location_id) : null,
      quantity: i.quantity || 1,
      tags: i.tags || null,
      updated_at: new Date(i.updated_at || Date.now()).toISOString()
    })),
    deleted_items: deletedItems,
    deleted_categories: deletedCategories,
    deleted_locations: deletedLocations
  };

  const response = await client.post('/sync/push', payload);
  const { item_id_map, category_id_map, location_id_map } = response.data;

  // Process completed deletions locally
  await db.transaction('rw', [db.items, db.categories, db.locations], async () => {
    // Delete local deleted markers
    await db.items.bulkDelete(deletedItems);
    await db.categories.bulkDelete(deletedCategories);
    await db.locations.bulkDelete(deletedLocations);

    // Translation maps for local primary key updates (old ID -> new ID)
    // 1. Categories Mapping
    for (const [tempId, serverId] of Object.entries(category_id_map)) {
      const record = await db.categories.get(tempId);
      if (record) {
        await db.categories.delete(tempId);
        await db.categories.put({
          ...record,
          id: serverId,
          sync_status: 'synced'
        });
      }
    }

    // 2. Locations Mapping
    for (const [tempId, serverId] of Object.entries(location_id_map)) {
      const record = await db.locations.get(tempId);
      if (record) {
        await db.locations.delete(tempId);
        await db.locations.put({
          ...record,
          id: serverId,
          sync_status: 'synced'
        });
      }
    }

    // After updating location IDs, translate local location parent_ids if they referenced temp location IDs
    const locs = await db.locations.toArray();
    for (const loc of locs) {
      if (loc.parent_id && String(loc.parent_id).startsWith('local_')) {
        const mappedParentId = location_id_map[loc.parent_id];
        if (mappedParentId) {
          await db.locations.update(loc.id, { parent_id: mappedParentId });
        }
      }
    }

    // 3. Items Mapping
    for (const [tempId, serverId] of Object.entries(item_id_map)) {
      const record = await db.items.get(tempId);
      if (record) {
        await db.items.delete(tempId);
        
        // Translate local category_id or location_id references if they were mapped
        let newCatId = record.category_id;
        if (newCatId && String(newCatId).startsWith('local_')) {
          newCatId = category_id_map[newCatId] || newCatId;
        }

        let newLocId = record.location_id;
        if (newLocId && String(newLocId).startsWith('local_')) {
          newLocId = location_id_map[newLocId] || newLocId;
        }

        await db.items.put({
          ...record,
          id: serverId,
          category_id: newCatId,
          location_id: newLocId,
          sync_status: 'synced'
        });
      }
    }

    // Mark non-temp active items as synced if not translated (edited existing items)
    const existingPushedItems = activeItems.filter(i => !String(i.id).startsWith('local_'));
    for (const item of existingPushedItems) {
      await db.items.update(item.id, { sync_status: 'synced' });
    }

    const existingPushedCategories = activeCategories.filter(c => !String(c.id).startsWith('local_'));
    for (const cat of existingPushedCategories) {
      await db.categories.update(cat.id, { sync_status: 'synced' });
    }

    const existingPushedLocations = activeLocations.filter(l => !String(l.id).startsWith('local_'));
    for (const loc of existingPushedLocations) {
      await db.locations.update(loc.id, { sync_status: 'synced' });
    }
  });

  return { count: totalPending, itemIdMap: item_id_map };
};

/**
 * Pulls latest database changes from the server.
 */
const pullServerChanges = async (lastSyncTime) => {
  // If the local database has no active items, force a full sync (ignore lastSyncTime)
  const localItemCount = await db.items.count();
  const effectiveSyncTime = localItemCount === 0 ? null : lastSyncTime;

  const response = await client.get('/sync/pull', {
    params: { last_sync_time: effectiveSyncTime || undefined }
  });

  const { items, categories, locations, active_ids, server_time } = response.data;
  let pullCount = items.length + categories.length + locations.length;

  console.log(`Server response: pulled ${pullCount} total updates.`);

  await db.transaction('rw', [db.items, db.categories, db.locations], async () => {
    // 1. Process Categories
    for (const cat of categories) {
      const localCat = await db.categories.get(cat.id);
      // Only write if local is not in pending offline edit status
      if (!localCat || localCat.sync_status === 'synced') {
        await db.categories.put({
          id: cat.id,
          name: cat.name,
          sync_status: 'synced',
          updated_at: new Date().toISOString()
        });
      }
    }

    // 2. Process Locations
    for (const loc of locations) {
      const localLoc = await db.locations.get(loc.id);
      if (!localLoc || localLoc.sync_status === 'synced') {
        await db.locations.put({
          id: loc.id,
          name: loc.name,
          parent_id: loc.parent_id,
          sync_status: 'synced',
          updated_at: new Date().toISOString()
        });
      }
    }

    // 3. Process Items
    for (const item of items) {
      const localItem = await db.items.get(item.id);
      if (!localItem || localItem.sync_status === 'synced') {
        // Build category_obj, location_obj to keep fully compatible with online items schema
        await db.items.put({
          id: item.id,
          name: item.name,
          category: item.category,
          category_id: item.category_id,
          location: item.location,
          location_id: item.location_id,
          quantity: item.quantity,
          tags: item.tags,
          image_path: item.image_path,
          thumbnail_path: item.thumbnail_path,
          created_at: item.created_at,
          updated_at: item.updated_at,
          sync_status: 'synced',
          category_obj: item.category_obj,
          location_obj: item.location_obj,
          tags_obj: item.tags_obj,
          local_image_blob: localItem ? localItem.local_image_blob : undefined
        });
      }
    }

    // 4. Run reconciliation to prune hard deleted records on the server
    if (active_ids) {
      // Prune Items
      const localItemIds = (await db.items.toArray())
        .map(i => i.id)
        .filter(id => !String(id).startsWith('local_'));
        
      const deletedItemIds = localItemIds.filter(id => !active_ids.items.includes(Number(id)));
      if (deletedItemIds.length > 0) {
        console.log(`Pruning ${deletedItemIds.length} hard deleted items locally.`);
        await db.items.bulkDelete(deletedItemIds);
      }

      // Prune Categories
      const localCatIds = (await db.categories.toArray())
        .map(c => c.id)
        .filter(id => !String(id).startsWith('local_'));
        
      const deletedCatIds = localCatIds.filter(id => !active_ids.categories.includes(Number(id)));
      if (deletedCatIds.length > 0) {
        console.log(`Pruning ${deletedCatIds.length} hard deleted categories locally.`);
        await db.categories.bulkDelete(deletedCatIds);
      }

      // Prune Locations
      const localLocIds = (await db.locations.toArray())
        .map(l => l.id)
        .filter(id => !String(id).startsWith('local_'));
        
      const deletedLocIds = localLocIds.filter(id => !active_ids.locations.includes(Number(id)));
      if (deletedLocIds.length > 0) {
        console.log(`Pruning ${deletedLocIds.length} hard deleted locations locally.`);
        await db.locations.bulkDelete(deletedLocIds);
      }
    }
  });

  return { count: pullCount, server_time };
};

/**
 * Uploads local Blobs (images captured offline) to synced server items.
 */
const uploadOfflineImages = async (itemIdMap = {}) => {
  // Find all items with unsynced local photos
  const localPhotoItems = (await db.items.toArray()).filter(i => i.local_image_blob !== undefined && i.local_image_blob !== null);
  
  if (localPhotoItems.length === 0) {
    return;
  }

  console.log(`Uploading ${localPhotoItems.length} offline camera photos to the server...`);

  for (const item of localPhotoItems) {
    let targetId = item.id;
    
    // If the item ID was mapped during the database push, translate it to get the server ID
    if (String(targetId).startsWith('local_')) {
      targetId = itemIdMap[targetId];
    }

    if (!targetId || String(targetId).startsWith('local_')) {
      // Defer image upload if the text item has not succeeded database push yet
      continue;
    }

    try {
      const formData = new FormData();
      formData.append('image', item.local_image_blob, 'offline_capture.jpg');

      const response = await client.post(`/items/${targetId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const updatedItem = response.data;

      // Update the local record with server paths and wipe the local Blob to conserve IndexedDB space
      await db.items.update(item.id, {
        id: updatedItem.id,
        image_path: updatedItem.image_path,
        thumbnail_path: updatedItem.thumbnail_path,
        local_image_blob: null,
        sync_status: 'synced'
      });

      console.log(`Successfully uploaded offline photo for item ${targetId}`);
    } catch (err) {
      console.error(`Failed to upload offline image for item ${targetId}:`, err);
    }
  }
};
